// src/components/organizer/events/EventViewModal.jsx
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchEventDetails } from "@/store/slices/organizerSlice";
import { 
  ArrowLeft, Calendar, MapPin, Users, DollarSign, 
  Route, Package, Clock, CheckCircle, Building2, Milestone,
  Pencil, ShieldCheck
} from "lucide-react";
import { getImageUrl } from "@/utils/imageUrl";
import { getComponentLabel } from "@/utils/constants";
import Badge from "@/components/common/Badge";
import Loading from "@/components/common/Loading";
import RouteDetailMap from "@/components/map/RouteDetailMap";

const EventViewModal = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentEvent, isLoading } = useSelector((state) => state.organizer);

  useEffect(() => {
    if (id) {
      dispatch(fetchEventDetails(id));
    }
  }, [dispatch, id]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      submitted: { tone: "info", label: "Submitted" },
      sponsor_listed: { tone: "info", label: "Sponsor Listed" },
      published: { tone: "success", label: "Published" },
      archived: { tone: "danger", label: "Archived" },
    };
    const config = statusConfig[status] || statusConfig.submitted;
    return <Badge tone={config.tone}>{config.label}</Badge>;
  };

  const getEventTypeBadge = (type) => {
    const types = {
      cycle_tour: "Cycle Tour",
      cycle_kumbh: "Cycle Kumbh",
      flash_mob: "Flash Mob",
      koset_ride: "Koset Ride",
    };
    return types[type] || type;
  };

  if (isLoading || !currentEvent) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  const canEdit = currentEvent.status === "submitted" || currentEvent.edit_override?.granted;
  const hasAdminOverride = currentEvent.edit_override?.granted && currentEvent.status !== "submitted";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              {currentEvent.title}
            </h1>
            {getStatusBadge(currentEvent.status)}
            {hasAdminOverride && (
              <Badge tone="success" size="sm">
                <span className="flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" />
                  Admin Authorized Editing
                </span>
              </Badge>
            )}
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Event Details
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => navigate(`/organizer/events/${currentEvent._id}/edit`)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg font-medium text-sm transition-all shadow-sm"
          >
            <Pencil className="w-4 h-4" />
            Edit Event
          </button>
        )}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
      >
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">
              Description
            </h3>
            <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
              {currentEvent.description}
            </p>
          </div>

          {/* Map Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="p-6 pb-0 mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-amber-600" />
                Route Map
              </h3>
            </div>
            <div className="h-[400px] w-full relative z-0">
               <RouteDetailMap 
                 route={{
                   coordinates: currentEvent.event_route?.coordinates?.coordinates || currentEvent.event_route?.coordinates || [],
                   startPoint: currentEvent.event_route?.start_point,
                   endPoint: currentEvent.event_route?.end_point
                 }} 
               />
            </div>
          </div>

          {/* Route Details */}
          {currentEvent.event_route && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Route className="w-5 h-5 text-amber-600" />
                Route Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {currentEvent.event_route.name && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Route Name</p>
                    <p className="font-medium text-gray-800 dark:text-gray-100">
                      {currentEvent.event_route.name}
                    </p>
                  </div>
                )}
                {currentEvent.event_route.distance && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Distance</p>
                    <p className="font-medium text-gray-800 dark:text-gray-100">
                      {currentEvent.event_route.distance} km
                    </p>
                  </div>
                )}
                {currentEvent.event_route.start_point && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Start Point</p>
                    <p className="font-medium text-gray-800 dark:text-gray-100">
                      {currentEvent.event_route.start_point}
                    </p>
                  </div>
                )}
                {currentEvent.event_route.end_point && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">End Point</p>
                    <p className="font-medium text-gray-800 dark:text-gray-100">
                      {currentEvent.event_route.end_point}
                    </p>
                  </div>
                )}
              </div>

              {/* Landmarks */}
              {currentEvent.event_route.landmarks?.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                  <h4 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-5 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-amber-500" />
                    Route Landmarks
                  </h4>
                  <div className="relative pl-3 space-y-5 before:absolute before:inset-y-0 before:left-[19px] before:w-[2px] before:bg-gradient-to-b before:from-amber-100 before:via-gray-100 before:to-gray-100 dark:before:from-amber-900/30 dark:before:via-gray-700 dark:before:to-gray-700 pointer-events-none">
                    {currentEvent.event_route.landmarks.map((lm, i) => (
                      <div key={i} className="relative flex items-start gap-4 pointer-events-auto group">
                        {/* Timeline Node */}
                        <div className="relative z-10 w-[14px] h-[14px] rounded-full bg-white dark:bg-gray-800 border-[3px] border-amber-400 dark:border-amber-500 shadow-[0_0_0_4px_white] dark:shadow-[0_0_0_4px_#1f2937] mt-[7px] shrink-0" />
                        
                        {/* Content */}
                        <div className="flex-1 bg-gray-50 dark:bg-gray-800/40 rounded-xl p-3.5 border border-gray-100 dark:border-gray-700/60 shadow-sm hover:border-amber-200 dark:hover:border-amber-900/50 hover:shadow-md transition-all duration-300">
                          <div className="flex flex-wrap items-center justify-between gap-y-1 gap-x-3 mb-1.5">
                            <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                              {lm.name}
                            </span>
                            {lm.km_mark != null && (
                              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded-md border border-amber-100/50 dark:border-amber-800/30 shrink-0">
                                <Milestone className="w-3.5 h-3.5" />
                                At {lm.km_mark} km
                              </span>
                            )}
                          </div>
                          {lm.description && (
                            <p className="text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed">
                              {lm.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sponsorship Components */}
          {currentEvent.sponsorship_components?.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-amber-600" />
                Sponsorship Components
              </h3>
              <div className="space-y-3">
                {currentEvent.sponsorship_components.map((component, index) => (
                  <div
                    key={component.id || index}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-800 dark:text-gray-100">
                          {getComponentLabel(component.category)}
                        </p>
                        {component.assigned_sponsor ? (
                          <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full flex items-center gap-1">
                            <CheckCircle className="w-3 h-3 text-green-600 dark:text-green-400" /> Assigned
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded-full">
                            Available
                          </span>
                        )}
                      </div>
                      {component.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {component.description}
                        </p>
                      )}
                      {/* Assigned Sponsor */}
                      {component.assigned_sponsor && (
                        <div className="flex flex-col gap-1 mt-2">
                          <div className="flex items-center gap-2">
                            {component.assigned_sponsor.logo_url ? (
                              <img
                                src={getImageUrl(component.assigned_sponsor.logo_url)}
                                alt={component.assigned_sponsor.company_name}
                                className="w-5 h-5 rounded-full object-cover border border-gray-200 dark:border-gray-600"
                              />
                            ) : (
                              <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                                <Building2 className="w-3 h-3 text-green-600 dark:text-green-400" />
                              </div>
                            )}
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                              <CheckCircle className="w-3 h-3" />
                              {component.assigned_sponsor.company_name}
                            </span>
                          </div>
                          {component.assigned_at && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 pl-7">
                              Assigned on: {new Date(component.assigned_at).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {component.type === "kind" ? (
                      <span className="shrink-0 ml-4 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full dark:bg-gray-700 dark:text-gray-300">
                        In-Kind
                      </span>
                    ) : component.value && (
                      <span className="text-amber-600 dark:text-amber-400 font-medium ml-4 shrink-0">
                        ₹{Number(component.value).toLocaleString()}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar Info */}
        <div className="space-y-6">
          {/* Quick Info Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Event Info
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Date</p>
                  <p className="font-medium text-gray-800 dark:text-gray-100">
                    {new Date(currentEvent.start_date).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                    {currentEvent.end_date && currentEvent.end_date !== currentEvent.start_date && (
                      <> - {new Date(currentEvent.end_date).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "long",
                      })}</>
                    )}
                  </p>
                </div>
              </div>

              {currentEvent.location?.city && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Location</p>
                    <p className="font-medium text-gray-800 dark:text-gray-100">
                      {[currentEvent.location.address, currentEvent.location.city]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Capacity</p>
                  <p className="font-medium text-gray-800 dark:text-gray-100">
                    {currentEvent.capacity === 0 ? "Unlimited" : `${currentEvent.capacity} participants`}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Event Type</p>
                  <p className="font-medium text-gray-800 dark:text-gray-100">
                    {getEventTypeBadge(currentEvent.event_type)}
                  </p>
                </div>
              </div>

              {currentEvent.is_paid && (
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Entry Fee</p>
                    <p className="font-medium text-gray-800 dark:text-gray-100">
                      ₹{currentEvent.entry_fee}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Registration Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                Registrations
              </h3>
              <div className="w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-3xl font-bold text-gray-900 dark:text-white">
                {currentEvent.registration_count || 0}
              </span>
              {currentEvent.capacity > 0 && (
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  / {currentEvent.capacity}
                </span>
              )}
            </div>

            {/* Progress Bar */}
            {currentEvent.capacity > 0 && (
              <div className="space-y-2">
                <div className="h-2.5 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(((currentEvent.registration_count || 0) / currentEvent.capacity) * 100, 100)}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="h-full bg-amber-600 dark:bg-amber-500 rounded-full"
                  />
                </div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 text-right">
                  {Math.round(((currentEvent.registration_count || 0) / currentEvent.capacity) * 100)}% Filled
                </p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EventViewModal;
